/**
 * `.cvsm` 的 manifest 结构（M1-15 / DESIGN.md §6.4）。
 *
 * §6.4 要求 manifest **人可读**。这不只是"用 JSON 而不是二进制"——
 * 字段名要能望文生义，数值用文档单位而不是内部编码，
 * 目的是格式出问题时人能打开文件看出哪里不对。
 *
 * **本模块不认识文档模型。** 分层规则里 io 在 document 之下，
 * 所以这里定义的是一份**独立的序列化契约**：纯 JSON 可表达的数据。
 * 由应用层负责在 `Document` 与它之间来回翻译——这也让格式的演进
 * 与内存模型的演进解耦，改一边不必动另一边。
 */

/** 当前格式版本。**不兼容的改动必须递增**，读取方据此决定能不能打开。 */
export const CVSM_VERSION = 1;

/** 容器内的固定路径（§6.4 的目录结构）。 */
export const CVSM_PATHS = {
  manifest: 'manifest.json',
  tiles: 'tiles/',
  resources: 'resources/',
  icc: 'resources/icc/',
  fonts: 'resources/fonts/',
  thumbnail: 'thumbnail.png',
} as const;

/** 块的压缩方式。写进 manifest，读取方据此选解码路径。 */
export type TileCompression = 'none' | 'deflate-raw';

export interface CvsmSize {
  readonly width: number;
  readonly height: number;
}

/** 序列化后的图层。字段与文档层的 `Layer` 对应，但**是另一套类型**。 */
/** 调整图层的参数（M2-20 起入容器）。io 层不解释语义，只保证形状合法。 */
export interface CvsmAdjustment {
  readonly kind: string;
  readonly params: Readonly<Record<string, number | readonly number[]>>;
}

/**
 * 图层效果（M3-14）。params 值域比调整图层宽——效果参数里有颜色（字符串）
 * 与位置枚举（字符串），所以允许 number | string。
 */
export interface CvsmEffect {
  readonly kind: string;
  readonly enabled: boolean;
  readonly params: Readonly<Record<string, number | string>>;
}

/**
 * 蒙版像素块的所有者 id。与应用层 maskLayerIdFor 的约定一致
 * （io 不能反向 import 应用层，所以这里是约定的权威副本，两边有测试对拍）。
 */
export function maskTileOwnerId(layerId: string): string {
  return `${layerId}#mask`;
}

/** 图层蒙版的元数据（M3-08 起入容器）。像素块以 `<层id>#mask` 为键入 tiles。 */
export interface CvsmMask {
  readonly enabled: boolean;
  readonly linked: boolean;
  /** 未分配块的蒙版值。缺省 255（全部显示）。 */
  readonly base?: 0 | 255;
  readonly bounds: { readonly x: number; readonly y: number; readonly width: number; readonly height: number };
}

/**
 * 矢量取色（补充：形状/文字的渐变填充+描边）。`string` 是历史格式
 * （只有纯色十六进制）——老文件必须继续读得出来，见 {@link parseVectorFill}。
 */
export type CvsmVectorFill =
  | string
  | { readonly type: 'solid'; readonly color: string; readonly alpha?: number }
  | { readonly type: 'gradient'; readonly stops: readonly { readonly offset: number; readonly color: string }[] };

export interface CvsmVectorStroke {
  readonly fill: CvsmVectorFill;
  readonly width: number;
}

/** 文本图层的可编辑源数据（M3-08）。栅格化结果照常走 tiles。 */
export interface CvsmTextRun {
  readonly text: string;
  readonly fontFamily: string;
  readonly fontSize: number;
  readonly fill: CvsmVectorFill;
  readonly stroke?: CvsmVectorStroke;
  readonly letterSpacing: number;
  readonly lineHeight: number;
}

export interface CvsmText {
  readonly runs: readonly CvsmTextRun[];
  readonly box: { readonly x: number; readonly y: number; readonly width: number; readonly height: number };
  readonly writingMode: 'horizontal' | 'vertical';
  /** 文字变形（对比 Photoshop 补全第一档缺口 阶段 5），只对横排生效。 */
  readonly warp?: { readonly style: 'arc' | 'bulge' | 'wave'; readonly bend: number };
}

/** 形状图层的路径与填充/描边（M3-15，描边+渐变补充）。 */
export interface CvsmShape {
  readonly commands: string;
  readonly fillRule: 'nonzero' | 'evenodd';
  readonly fill?: CvsmVectorFill;
  readonly stroke?: CvsmVectorStroke;
}

/** 智能滤镜链的一项（M4-06）。 */
export interface CvsmSmartFilter {
  readonly kind: string;
  readonly enabled: boolean;
  readonly params: Readonly<Record<string, number | string>>;
}

/**
 * 智能对象的持久化状态（M4-06 / ADR-0008）。只存 `bounds` 与 `filters`——
 * `source`（内嵌文档/原始文件字节）暂不持久化，见 ADR-0008 补记。
 */
export interface CvsmSmartObject {
  readonly bounds: { readonly x: number; readonly y: number; readonly width: number; readonly height: number };
  readonly filters: readonly CvsmSmartFilter[];
}

export interface CvsmLayer {
  readonly id: string;
  readonly name: string;
  readonly kind: string;
  readonly visible: boolean;
  readonly opacity: number;
  readonly fillOpacity: number;
  readonly blendMode: string;
  readonly clipped: boolean;
  /** a,b,c,d,e,f 六元组。 */
  readonly transform: readonly [number, number, number, number, number, number];
  readonly children?: readonly CvsmLayer[];
  readonly passThrough?: boolean;
  /** 该图层在容器里有哪些块。空数组表示没有像素（组、空图层）。 */
  readonly tiles?: readonly CvsmTileRef[];
  /** kind 为 adjustment 时的参数。 */
  readonly adjustment?: CvsmAdjustment;
  /** 图层蒙版元数据（M3-08）。 */
  readonly mask?: CvsmMask;
  /** 蒙版像素块索引。块的所有者 id 为 {@link maskTileOwnerId}。 */
  readonly maskTiles?: readonly CvsmTileRef[];
  /** kind 为 text 时的可编辑源（M3-08）。 */
  readonly text?: CvsmText;
  /** 图层样式列表（M3-14）。任意图层种类都可以有。 */
  readonly effects?: readonly CvsmEffect[];
  /** kind 为 shape 时的路径与填充（M3-15）。 */
  readonly shape?: CvsmShape;
  /** kind 为 smartObject 时的滤镜链与边界（M4-06）。 */
  readonly smartObject?: CvsmSmartObject;
}

/** 一个块在容器里的位置。路径由 `tilePath()` 生成，这里存的是索引信息。 */
export interface CvsmTileRef {
  readonly lod: number;
  readonly tx: number;
  readonly ty: number;
  /** 未压缩字节数，用于读取时预分配与校验。 */
  readonly bytes: number;
  /**
   * 内容哈希。**增量保存的判据**：哈希没变就不重写这个块。
   *
   * 用内容哈希而不是"脏标记"，是因为脏标记只在本次会话里有效——
   * 打开一个已存在的文件再保存时，没有任何块是"脏"的，
   * 但我们仍要知道哪些块与磁盘上的一致。
   */
  readonly hash: string;
}

/** 图层复合的一条图层快照（M4-05）。字段与文档层 `LayerCompEntry` 对应。 */
export interface CvsmLayerCompEntry {
  readonly layerId: string;
  readonly visible: boolean;
  readonly opacity: number;
  readonly transform: readonly [number, number, number, number, number, number];
  readonly effectsEnabled: readonly boolean[];
}

export interface CvsmLayerComp {
  readonly id: string;
  readonly name: string;
  readonly entries: readonly CvsmLayerCompEntry[];
}

export interface CvsmManifest {
  readonly version: number;
  readonly id: string;
  readonly size: CvsmSize;
  /** 块边长（像素）。写进文件而不是硬编码，将来改 TILE 尺寸时老文件仍能读。 */
  readonly tileSize: number;
  readonly compression: TileCompression;
  readonly createdAt: string;
  readonly modifiedAt: string;
  readonly root: CvsmLayer;
  /** 图层复合列表（M4-05）。缺省视为空数组——老文件没有这个字段。 */
  readonly layerComps?: readonly CvsmLayerComp[];
  /** 应用与格式的版本信息，排查兼容性问题时有用。 */
  readonly generator?: string;
}

/** 块在容器里的路径。`tiles/<layerId>/<lod>_<tx>_<ty>.bin`（§6.4）。 */
export function tilePath(layerId: string, lod: number, tx: number, ty: number): string {
  return `${CVSM_PATHS.tiles}${encodeSegment(layerId)}/${lod}_${tx}_${ty}.bin`;
}

/**
 * 图层 id 用作路径段时要转义。
 *
 * 图层名/id 可能含 `/`、`..`、控制字符。不转义的话，一个叫 `../../etc` 的图层
 * 就能让写出去的条目跑到容器外的路径上——解压工具照着写就是目录穿越。
 * 用 encodeURIComponent 而不是自定义转义：它的可逆性有规范保证。
 */
export function encodeSegment(value: string): string {
  return encodeURIComponent(value);
}

export function decodeSegment(value: string): string {
  return decodeURIComponent(value);
}

export class CvsmFormatError extends Error {
  constructor(message: string) {
    super(`.cvsm 格式错误：${message}`);
    this.name = 'CvsmFormatError';
  }
}

/**
 * 校验 manifest。
 *
 * 从文件读来的 JSON 是**不可信输入**：任何字段都可能缺失、类型错误或是恶意值。
 * 这里逐字段检查后再交给上层，比让上层在用到某个字段时才崩要好定位得多。
 */
export function parseManifest(json: unknown): CvsmManifest {
  if (typeof json !== 'object' || json === null) {
    throw new CvsmFormatError('manifest 不是对象');
  }
  const raw = json as Record<string, unknown>;

  const version = raw['version'];
  if (typeof version !== 'number' || !Number.isInteger(version)) {
    throw new CvsmFormatError('缺少合法的 version');
  }
  if (version > CVSM_VERSION) {
    throw new CvsmFormatError(
      `文件版本 ${version} 高于本程序支持的 ${CVSM_VERSION}，请升级后再打开`,
    );
  }

  const size = raw['size'];
  if (typeof size !== 'object' || size === null) throw new CvsmFormatError('缺少 size');
  const { width, height } = size as Record<string, unknown>;
  if (!isPositiveInt(width) || !isPositiveInt(height)) {
    throw new CvsmFormatError('size 的宽高必须是正整数');
  }

  const tileSize = raw['tileSize'];
  if (!isPositiveInt(tileSize)) throw new CvsmFormatError('tileSize 必须是正整数');

  const compression = raw['compression'];
  if (compression !== 'none' && compression !== 'deflate-raw') {
    throw new CvsmFormatError(`不支持的压缩方式 ${String(compression)}`);
  }

  const id = raw['id'];
  if (typeof id !== 'string' || id === '') throw new CvsmFormatError('缺少 id');

  return {
    version,
    id,
    size: { width, height },
    tileSize,
    compression,
    createdAt: typeof raw['createdAt'] === 'string' ? raw['createdAt'] : '',
    modifiedAt: typeof raw['modifiedAt'] === 'string' ? raw['modifiedAt'] : '',
    root: parseLayer(raw['root']),
    ...(raw['layerComps'] === undefined ? {} : { layerComps: parseLayerComps(raw['layerComps']) }),
    ...(typeof raw['generator'] === 'string' ? { generator: raw['generator'] } : {}),
  };
}

const MAX_LAYER_COMPS = 256;

function parseLayerComps(json: unknown): CvsmLayerComp[] {
  if (!Array.isArray(json) || json.length > MAX_LAYER_COMPS) {
    throw new CvsmFormatError(`layerComps 必须是不超过 ${MAX_LAYER_COMPS} 项的数组`);
  }
  return json.map((entry, index) => {
    if (typeof entry !== 'object' || entry === null) {
      throw new CvsmFormatError(`layerComps[${index}] 不是对象`);
    }
    const raw = entry as Record<string, unknown>;
    const id = raw['id'];
    if (typeof id !== 'string' || id === '') throw new CvsmFormatError(`layerComps[${index}] 缺少 id`);
    const name = typeof raw['name'] === 'string' ? raw['name'] : id;
    const rawEntries = raw['entries'];
    if (!Array.isArray(rawEntries)) throw new CvsmFormatError(`复合「${id}」缺少 entries 数组`);
    return {
      id,
      name,
      entries: rawEntries.map((e, i) => parseLayerCompEntry(e, id, i)),
    };
  });
}

function parseLayerCompEntry(json: unknown, compId: string, index: number): CvsmLayerCompEntry {
  if (typeof json !== 'object' || json === null) {
    throw new CvsmFormatError(`复合「${compId}」的 entries[${index}] 不是对象`);
  }
  const raw = json as Record<string, unknown>;
  const layerId = raw['layerId'];
  if (typeof layerId !== 'string' || layerId === '') {
    throw new CvsmFormatError(`复合「${compId}」的 entries[${index}] 缺少 layerId`);
  }
  const transform = raw['transform'];
  if (!Array.isArray(transform) || transform.length !== 6 || !transform.every(isFiniteNumber)) {
    throw new CvsmFormatError(`复合「${compId}」的 entries[${index}] 的 transform 必须是 6 个有限数`);
  }
  const effectsEnabled = raw['effectsEnabled'];
  if (!Array.isArray(effectsEnabled) || !effectsEnabled.every((v) => typeof v === 'boolean')) {
    throw new CvsmFormatError(`复合「${compId}」的 entries[${index}] 的 effectsEnabled 必须是布尔数组`);
  }
  return {
    layerId,
    visible: raw['visible'] !== false,
    opacity: clamp01(raw['opacity']),
    transform: transform as unknown as readonly [number, number, number, number, number, number],
    effectsEnabled: effectsEnabled as boolean[],
  };
}

function parseLayer(json: unknown): CvsmLayer {
  if (typeof json !== 'object' || json === null) throw new CvsmFormatError('图层不是对象');
  const raw = json as Record<string, unknown>;

  const id = raw['id'];
  const kind = raw['kind'];
  if (typeof id !== 'string' || id === '') throw new CvsmFormatError('图层缺少 id');
  if (typeof kind !== 'string') throw new CvsmFormatError(`图层「${id}」缺少 kind`);

  const transform = raw['transform'];
  if (!Array.isArray(transform) || transform.length !== 6 || !transform.every(isFiniteNumber)) {
    throw new CvsmFormatError(`图层「${id}」的 transform 必须是 6 个有限数`);
  }

  const children = raw['children'];
  const tiles = raw['tiles'];

  return {
    id,
    kind,
    name: typeof raw['name'] === 'string' ? raw['name'] : id,
    visible: raw['visible'] !== false,
    opacity: clamp01(raw['opacity']),
    fillOpacity: clamp01(raw['fillOpacity']),
    blendMode: typeof raw['blendMode'] === 'string' ? raw['blendMode'] : 'normal',
    clipped: raw['clipped'] === true,
    transform: transform as unknown as readonly [number, number, number, number, number, number],
    ...(Array.isArray(children) ? { children: children.map(parseLayer) } : {}),
    ...(typeof raw['passThrough'] === 'boolean' ? { passThrough: raw['passThrough'] } : {}),
    ...(Array.isArray(tiles) ? { tiles: tiles.map((t) => parseTileRef(t, id)) } : {}),
    ...(Array.isArray(raw['maskTiles'])
      ? { maskTiles: (raw['maskTiles'] as unknown[]).map((t) => parseTileRef(t, id)) }
      : {}),
    ...(raw['adjustment'] === undefined ? {} : { adjustment: parseAdjustment(raw['adjustment'], id) }),
    ...(raw['mask'] === undefined ? {} : { mask: parseMask(raw['mask'], id) }),
    ...(raw['text'] === undefined ? {} : { text: parseText(raw['text'], id) }),
    ...(raw['effects'] === undefined ? {} : { effects: parseEffects(raw['effects'], id) }),
    ...(raw['shape'] === undefined ? {} : { shape: parseShape(raw['shape'], id) }),
    ...(raw['smartObject'] === undefined ? {} : { smartObject: parseSmartObject(raw['smartObject'], id) }),
  };
}

function parseSmartObject(json: unknown, layerId: string): CvsmSmartObject {
  if (typeof json !== 'object' || json === null) {
    throw new CvsmFormatError(`图层「${layerId}」的 smartObject 不是对象`);
  }
  const raw = json as Record<string, unknown>;
  const bounds = raw['bounds'];
  if (typeof bounds !== 'object' || bounds === null) {
    throw new CvsmFormatError(`图层「${layerId}」的 smartObject.bounds 缺失`);
  }
  const { x, y, width, height } = bounds as Record<string, unknown>;
  if (!isFiniteNumber(x) || !isFiniteNumber(y) || !isPositiveInt(width) || !isPositiveInt(height)) {
    throw new CvsmFormatError(`图层「${layerId}」的 smartObject.bounds 字段非法`);
  }
  const filters = raw['filters'];
  if (!Array.isArray(filters)) {
    throw new CvsmFormatError(`图层「${layerId}」的 smartObject.filters 必须是数组`);
  }
  return {
    bounds: { x, y, width, height },
    filters: filters.map((f, i) => parseSmartFilter(f, layerId, i)),
  };
}

function parseSmartFilter(json: unknown, layerId: string, index: number): CvsmSmartFilter {
  if (typeof json !== 'object' || json === null) {
    throw new CvsmFormatError(`图层「${layerId}」的 smartObject.filters[${index}] 不是对象`);
  }
  const raw = json as Record<string, unknown>;
  const kind = raw['kind'];
  if (typeof kind !== 'string' || kind === '') {
    throw new CvsmFormatError(`图层「${layerId}」的 smartObject.filters[${index}] 缺少 kind`);
  }
  const rawParams = raw['params'];
  if (typeof rawParams !== 'object' || rawParams === null) {
    throw new CvsmFormatError(`图层「${layerId}」的 smartObject.filters[${index}] 缺少 params`);
  }
  const params: Record<string, number | string> = {};
  for (const [key, value] of Object.entries(rawParams as Record<string, unknown>)) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      params[key] = value;
    } else if (typeof value === 'string') {
      params[key] = value;
    } else {
      throw new CvsmFormatError(
        `图层「${layerId}」的 smartObject.filters[${index}] 参数「${key}」必须是有限数或字符串`,
      );
    }
  }
  return { kind, enabled: raw['enabled'] !== false, params };
}

/** §13.5 上限：路径命令字符串/渐变色标数没有理由无限长。 */
const MAX_PATH_COMMANDS_LENGTH = 1_000_000;
const MAX_GRADIENT_STOPS = 64;

/**
 * 校验一份矢量取色。`undefined` 原样放过（"没有这个字段"合法，调用方
 * 决定缺省行为）；旧文件的纯色字符串按 {@link CvsmVectorFill} 的 `string`
 * 分支放过，不强行升级成新形状——写回时才统一写新格式，读旧文件不该炸。
 */
function parseVectorFill(json: unknown, where: string): CvsmVectorFill | undefined {
  if (json === undefined) return undefined;
  if (typeof json === 'string') return json;
  if (typeof json !== 'object' || json === null) {
    throw new CvsmFormatError(`${where} 必须是字符串或 { type, ... } 对象`);
  }
  const raw = json as Record<string, unknown>;
  if (raw['type'] === 'solid') {
    if (typeof raw['color'] !== 'string') throw new CvsmFormatError(`${where}.color 必须是字符串`);
    const alpha = raw['alpha'];
    if (alpha !== undefined && (!isFiniteNumber(alpha) || alpha < 0 || alpha > 1)) {
      throw new CvsmFormatError(`${where}.alpha 必须是 0–1 的有限数`);
    }
    return { type: 'solid', color: raw['color'], ...(alpha === undefined ? {} : { alpha }) };
  }
  if (raw['type'] === 'gradient') {
    const rawStops = raw['stops'];
    if (!Array.isArray(rawStops) || rawStops.length === 0 || rawStops.length > MAX_GRADIENT_STOPS) {
      throw new CvsmFormatError(`${where}.stops 必须是 1–${MAX_GRADIENT_STOPS} 项的数组`);
    }
    const stops = rawStops.map((entry, index) => {
      if (typeof entry !== 'object' || entry === null) {
        throw new CvsmFormatError(`${where}.stops[${index}] 不是对象`);
      }
      const stop = entry as Record<string, unknown>;
      if (!isFiniteNumber(stop['offset']) || typeof stop['color'] !== 'string') {
        throw new CvsmFormatError(`${where}.stops[${index}] 非法`);
      }
      return { offset: stop['offset'], color: stop['color'] };
    });
    return { type: 'gradient', stops };
  }
  throw new CvsmFormatError(`${where}.type 必须是 solid 或 gradient`);
}

function parseVectorStroke(json: unknown, where: string): CvsmVectorStroke | undefined {
  if (json === undefined) return undefined;
  if (typeof json !== 'object' || json === null) {
    throw new CvsmFormatError(`${where} 不是对象`);
  }
  const raw = json as Record<string, unknown>;
  const fill = parseVectorFill(raw['fill'], `${where}.fill`);
  if (fill === undefined) throw new CvsmFormatError(`${where}.fill 缺失`);
  if (!isFiniteNumber(raw['width']) || raw['width'] < 0) {
    throw new CvsmFormatError(`${where}.width 非法`);
  }
  return { fill, width: raw['width'] };
}

function parseShape(json: unknown, layerId: string): CvsmShape {
  if (typeof json !== 'object' || json === null) {
    throw new CvsmFormatError(`图层「${layerId}」的 shape 不是对象`);
  }
  const raw = json as Record<string, unknown>;
  const commands = raw['commands'];
  if (typeof commands !== 'string' || commands.length > MAX_PATH_COMMANDS_LENGTH) {
    throw new CvsmFormatError(`图层「${layerId}」的 shape.commands 非法`);
  }
  const fillRule = raw['fillRule'];
  const fill = parseVectorFill(raw['fill'], `图层「${layerId}」的 shape.fill`);
  const stroke = parseVectorStroke(raw['stroke'], `图层「${layerId}」的 shape.stroke`);
  return {
    commands,
    fillRule: fillRule === 'evenodd' ? 'evenodd' : 'nonzero',
    ...(fill === undefined ? {} : { fill }),
    ...(stroke === undefined ? {} : { stroke }),
  };
}

const MAX_EFFECTS = 32;

function parseEffects(json: unknown, layerId: string): CvsmEffect[] {
  if (!Array.isArray(json) || json.length > MAX_EFFECTS) {
    throw new CvsmFormatError(`图层「${layerId}」的 effects 必须是不超过 ${MAX_EFFECTS} 项的数组`);
  }
  return json.map((entry, index) => {
    if (typeof entry !== 'object' || entry === null) {
      throw new CvsmFormatError(`图层「${layerId}」的 effects[${index}] 不是对象`);
    }
    const raw = entry as Record<string, unknown>;
    const kind = raw['kind'];
    if (typeof kind !== 'string' || kind === '') {
      throw new CvsmFormatError(`图层「${layerId}」的 effects[${index}] 缺少 kind`);
    }
    const rawParams = raw['params'];
    if (typeof rawParams !== 'object' || rawParams === null) {
      throw new CvsmFormatError(`图层「${layerId}」的 effects[${index}] 缺少 params`);
    }
    const params: Record<string, number | string> = {};
    for (const [key, value] of Object.entries(rawParams as Record<string, unknown>)) {
      if (typeof value === 'number' && Number.isFinite(value)) {
        params[key] = value;
      } else if (typeof value === 'string') {
        params[key] = value;
      } else {
        throw new CvsmFormatError(
          `图层「${layerId}」的 effects[${index}] 参数「${key}」必须是有限数或字符串`,
        );
      }
    }
    return { kind, enabled: raw['enabled'] !== false, params };
  });
}

function parseRect(
  json: unknown,
  what: string,
): { x: number; y: number; width: number; height: number } {
  if (typeof json !== 'object' || json === null) throw new CvsmFormatError(`${what} 不是对象`);
  const raw = json as Record<string, unknown>;
  const { x, y, width, height } = raw;
  if (![x, y, width, height].every(isFiniteNumber)) {
    throw new CvsmFormatError(`${what} 的坐标必须是有限数`);
  }
  return { x: x as number, y: y as number, width: width as number, height: height as number };
}

function parseMask(json: unknown, layerId: string): CvsmMask {
  if (typeof json !== 'object' || json === null) {
    throw new CvsmFormatError(`图层「${layerId}」的 mask 不是对象`);
  }
  const raw = json as Record<string, unknown>;
  const base = raw['base'];
  if (base !== undefined && base !== 0 && base !== 255) {
    throw new CvsmFormatError(`图层「${layerId}」的蒙版 base 必须是 0 或 255`);
  }
  return {
    enabled: raw['enabled'] !== false,
    linked: raw['linked'] !== false,
    ...(base === undefined ? {} : { base }),
    bounds: parseRect(raw['bounds'], `图层「${layerId}」的蒙版 bounds`),
  };
}

/** §13.5 的防御上限：文本不设限会让一份恶意 manifest 撑爆内存。 */
const MAX_TEXT_RUNS = 1000;
const MAX_RUN_TEXT_LENGTH = 100_000;

function parseText(json: unknown, layerId: string): CvsmText {
  if (typeof json !== 'object' || json === null) {
    throw new CvsmFormatError(`图层「${layerId}」的 text 不是对象`);
  }
  const raw = json as Record<string, unknown>;
  const rawRuns = raw['runs'];
  if (!Array.isArray(rawRuns) || rawRuns.length > MAX_TEXT_RUNS) {
    throw new CvsmFormatError(`图层「${layerId}」的 runs 必须是不超过 ${MAX_TEXT_RUNS} 项的数组`);
  }
  const runs = rawRuns.map((entry, index): CvsmTextRun => {
    if (typeof entry !== 'object' || entry === null) {
      throw new CvsmFormatError(`图层「${layerId}」的 runs[${index}] 不是对象`);
    }
    const run = entry as Record<string, unknown>;
    const text = run['text'];
    const fontFamily = run['fontFamily'];
    const fontSize = run['fontSize'];
    if (typeof text !== 'string' || text.length > MAX_RUN_TEXT_LENGTH) {
      throw new CvsmFormatError(`图层「${layerId}」的 runs[${index}].text 非法`);
    }
    if (typeof fontFamily !== 'string' || fontFamily === '') {
      throw new CvsmFormatError(`图层「${layerId}」的 runs[${index}].fontFamily 非法`);
    }
    if (!isFiniteNumber(fontSize) || fontSize <= 0 || fontSize > 10_000) {
      throw new CvsmFormatError(`图层「${layerId}」的 runs[${index}].fontSize 非法`);
    }
    const lineHeight = run['lineHeight'];
    const fill = parseVectorFill(run['fill'], `图层「${layerId}」的 runs[${index}].fill`) ?? '#000000';
    const stroke = parseVectorStroke(run['stroke'], `图层「${layerId}」的 runs[${index}].stroke`);
    return {
      text,
      fontFamily,
      fontSize,
      fill,
      ...(stroke === undefined ? {} : { stroke }),
      letterSpacing: isFiniteNumber(run['letterSpacing']) ? run['letterSpacing'] : 0,
      lineHeight: isFiniteNumber(lineHeight) && lineHeight > 0 ? lineHeight : 1.6,
    };
  });
  const writingMode = raw['writingMode'];
  const warp = parseTextWarp(raw['warp']);
  return {
    runs,
    box: parseRect(raw['box'], `图层「${layerId}」的文本框`),
    writingMode: writingMode === 'vertical' ? 'vertical' : 'horizontal',
    ...(warp === undefined ? {} : { warp }),
  };
}

/** 畸形的 warp（不认识的 style/非有限数 bend）当作没有变形，不炸整份文档。 */
function parseTextWarp(
  json: unknown,
): { readonly style: 'arc' | 'bulge' | 'wave'; readonly bend: number } | undefined {
  if (typeof json !== 'object' || json === null) return undefined;
  const raw = json as Record<string, unknown>;
  const style = raw['style'];
  const bend = raw['bend'];
  if (style !== 'arc' && style !== 'bulge' && style !== 'wave') return undefined;
  if (!isFiniteNumber(bend)) return undefined;
  return { style, bend };
}

/** 防御性解析：params 只允许有限数或有限数数组，其他一律拒绝（§13.5 的纪律）。 */
function parseAdjustment(json: unknown, layerId: string): CvsmAdjustment {
  if (typeof json !== 'object' || json === null) {
    throw new CvsmFormatError(`图层「${layerId}」的 adjustment 不是对象`);
  }
  const raw = json as Record<string, unknown>;
  const kind = raw['kind'];
  if (typeof kind !== 'string' || kind === '') {
    throw new CvsmFormatError(`图层「${layerId}」的 adjustment 缺少 kind`);
  }
  const rawParams = raw['params'];
  if (typeof rawParams !== 'object' || rawParams === null) {
    throw new CvsmFormatError(`图层「${layerId}」的 adjustment 缺少 params`);
  }
  const params: Record<string, number | readonly number[]> = {};
  for (const [key, value] of Object.entries(rawParams as Record<string, unknown>)) {
    if (isFiniteNumber(value)) {
      params[key] = value;
    } else if (Array.isArray(value) && value.every(isFiniteNumber)) {
      params[key] = value as number[];
    } else {
      throw new CvsmFormatError(`图层「${layerId}」的调整参数「${key}」必须是有限数或其数组`);
    }
  }
  return { kind, params };
}

function parseTileRef(json: unknown, layerId: string): CvsmTileRef {
  if (typeof json !== 'object' || json === null) {
    throw new CvsmFormatError(`图层「${layerId}」的块条目不是对象`);
  }
  const raw = json as Record<string, unknown>;
  const { lod, tx, ty, bytes } = raw;
  if (!isNonNegativeInt(lod) || !isNonNegativeInt(tx) || !isNonNegativeInt(ty)) {
    throw new CvsmFormatError(`图层「${layerId}」的块坐标必须是非负整数`);
  }
  if (!isPositiveInt(bytes)) {
    throw new CvsmFormatError(`图层「${layerId}」的块 ${lod}_${tx}_${ty} 缺少合法的 bytes`);
  }
  const hash = raw['hash'];
  if (typeof hash !== 'string' || hash === '') {
    throw new CvsmFormatError(`图层「${layerId}」的块 ${lod}_${tx}_${ty} 缺少 hash`);
  }
  return { lod, tx, ty, bytes, hash };
}

function isPositiveInt(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0;
}

function isNonNegativeInt(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function clamp01(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 1;
  return Math.min(1, Math.max(0, value));
}
